import { UserLatestAssessmentResponse } from '@api/assessmentQuestionnaireApiHandler';
import IndicatorIcon from '@assets/images/icons/indicator-icon.svg';
import { DEFAULT_V2_IMAGE_PATH } from '@config/questionnaire-v2-images.config';
import { colorMap, defaultColors } from '@helpers/color.helper';
import { MotionImage, MotionText } from '@shared/atoms';
import React from 'react';
import { useHistory } from 'react-router';
import styles from './index.module.scss';

interface CorePillarScoresProps {
  pillars: UserLatestAssessmentResponse[];
  nextAssessment: UserLatestAssessmentResponse | null;
}

export const CorePillarScores: React.FC<CorePillarScoresProps> = ({ pillars, nextAssessment }) => {
  const history = useHistory();

  const getScoreDegree = (sc: number) => (sc / 100) * 360;

  return (
    <div className={styles.pillarsSection}>
      <MotionText className={styles.subsectionTitle} as="h4" text="Core pillar scores" />

      <div className={styles.pillarsGrid}>
        {pillars.map((pillar) => (
          <div
            key={pillar.name}
            className={styles.pillarCard}
            style={{
              backgroundColor: pillar?.latestAssessment
                ? pillar?.color
                  ? `${colorMap[pillar?.color]?.lightColor}`
                  : defaultColors?.lightColor
                : defaultColors?.mediumColor,
              border: `1px solid ${
                pillar?.latestAssessment
                  ? pillar?.color
                    ? `${colorMap[pillar?.color]?.darkColor}`
                    : defaultColors?.darkColor
                  : defaultColors?.darkColor
              }`,
            }}
            onClick={() => {
              if (nextAssessment) {
                if (pillar?.latestAssessment?.questionnaireId) {
                  // history.push(
                  //   `/assessment-scores?questionnaireId=${pillar?.latestAssessment?.questionnaireId}&nextAssessmentId=${nextAssessment?.latestAssessment?.questionnaireId}&nextAssessment=${nextAssessment?.latestAssessment?.questionnaireV2?.name}`,
                  // );
                  history.push(
                    `/assessment-scores?questionnaireId=${pillar?.latestAssessment?.questionnaireId}`,
                  );
                } else {
                  history.push(`/assessment?questionnaireId=${pillar?.id}`);
                }
              } else if (!nextAssessment) {
                if (pillar?.latestAssessment?.questionnaireId) {
                  history.push(
                    `/assessment-scores?questionnaireId=${pillar?.latestAssessment?.questionnaireId}`,
                  );
                } else {
                  history.push(`/assessment?questionnaireId=${pillar?.id}`);
                }
              }
            }}
          >
            <MotionImage
              src={pillar?.icon ? `${DEFAULT_V2_IMAGE_PATH}/${pillar?.icon}` : IndicatorIcon}
              alt=""
              aria-hidden="true"
              className={styles.pillarIcon}
            />

            <MotionText className={styles.pillarName} as="h5" text={pillar.name} />

            <div className={styles.scoreCircle}>
              {/* TODO: CHECK UNLOCK | LOCK */}
              {pillar?.latestAssessment ? (
                <>
                  <div
                    className={styles.scoreRing}
                    style={{
                      background: `conic-gradient(${
                        pillar?.color
                          ? `${colorMap[pillar?.color]?.darkColor}`
                          : defaultColors?.darkColor
                      } 0deg ${getScoreDegree(
                        pillar?.latestAssessment?.totalScore || 0,
                      )}deg, #f3f4f6 ${getScoreDegree(
                        pillar?.latestAssessment?.totalScore || 0,
                      )}deg 360deg)`,
                      borderColor: pillar?.color
                        ? `${colorMap[pillar?.color]?.darkColor}`
                        : defaultColors?.darkColor,
                    }}
                  />
                  <div
                    className={styles.scoreInnerCircle}
                    style={{
                      background: pillar?.color
                        ? `${colorMap[pillar?.color]?.lightColor}`
                        : defaultColors?.lightColor,
                    }}
                  />
                  <div className={styles.scoreValue}>
                    <MotionText
                      className={styles.scoreNumber}
                      as="h5"
                      text={
                        pillar?.latestAssessment?.totalScore
                          ? Number.isInteger(pillar?.latestAssessment?.totalScore)
                            ? pillar?.latestAssessment?.totalScore
                            : parseFloat(pillar?.latestAssessment?.totalScore.toFixed(2))
                          : 0
                      }
                    />

                    <span className={styles.scoreTotal}>/{pillar?.maxScore || 100}</span>
                  </div>
                </>
              ) : (
                <div className={styles.lockedScore}>
                  <div className={styles.lockOuterCircle} />
                  <div className={styles.lockIconContainer}>
                    <svg
                      width="25"
                      height="25"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.79844 11.7333V10.5143C8.79844 6.46242 12.0099 3.20001 15.9984 3.20001C19.987 3.20001 23.1984 6.46242 23.1984 10.5143V11.7333M8.79844 11.7333C7.47844 11.7333 6.39844 12.8305 6.39844 14.1714V26.3619C6.39844 27.7029 7.47844 28.8 8.79844 28.8H23.1984C24.5184 28.8 25.5984 27.7029 25.5984 26.3619V14.1714C25.5984 12.8305 24.5184 11.7333 23.1984 11.7333M8.79844 11.7333H23.1984M15.9984 17.8286C17.3184 17.8286 18.3984 18.9257 18.3984 20.2667C18.3984 21.6076 17.3184 22.7048 15.9984 22.7048C14.6784 22.7048 13.5984 21.6076 13.5984 20.2667C13.5984 18.9257 14.6784 17.8286 15.9984 17.8286Z"
                        stroke="#505050"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.insightsLink}>
              {pillar?.latestAssessment ? (
                <a
                  href={`/assessment-scores?questionnaireId=${pillar?.latestAssessment?.questionnaireId}`}
                  className={styles.viewInsightsLink}
                >
                  View insights &gt;
                </a>
              ) : (
                <a
                  href={`/assessment?questionnaireId=${pillar?.id}`}
                  className={styles.unlockInsightsLink}
                >
                  Unlock insights &gt;
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
